/** QuickPlan — Caches three.js : géométries (par primitive résolue), arêtes et matériaux.
 *  Deux profilés de même section et longueur partagent la même géométrie. */
import * as THREE from 'three';
import { resolveSection } from '../../shared/sections.js';

const geos = new Map();
const edges = new Map();
const mats = new Map();
const LIMIT = 3000;

/** Matrices de permutation section → repère local (cf. extrudeMap dans shared/resolve.js). */
const AXIS_MATRIX = {
  x: new THREE.Matrix4().set(0, 0, 1, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 1),
  y: new THREE.Matrix4().set(0, 1, 0, 0, 0, 0, 1, 0, 1, 0, 0, 0, 0, 0, 0, 1),
  z: new THREE.Matrix4(),
};

const r = (v) => Math.round(v * 100) / 100;

export function partKey(p) {
  if (p.type === 'box') return `b:${p.size.map(r).join(':')}`;
  if (p.type === 'cyl') return `c:${r(p.r)}:${r(p.h)}:${p.axis}`;
  const sec = typeof p.section === 'string' ? p.section : JSON.stringify(p.section);
  return `e:${sec}:${r(p.length)}:${p.axis}`;
}

function build(p) {
  if (p.type === 'box') {
    const [x, y, z] = p.size;
    return new THREE.BoxGeometry(x, y, z).translate(x / 2, y / 2, z / 2);
  }
  if (p.type === 'cyl') {
    const seg = p.r > 60 ? 48 : 24;
    const g = new THREE.CylinderGeometry(p.r, p.r, p.h, seg); // le long de Y, centré
    if (p.axis === 'z') return g.rotateX(Math.PI / 2).translate(0, 0, p.h / 2);
    if (p.axis === 'x') return g.rotateZ(-Math.PI / 2).translate(p.h / 2, 0, 0);
    return g.translate(0, p.h / 2, 0);
  }
  const sec = resolveSection(p.section);
  const shape = new THREE.Shape(sec.outer.map(([x, y]) => new THREE.Vector2(x, y)));
  for (const h of sec.holes) shape.holes.push(new THREE.Path(h.map(([x, y]) => new THREE.Vector2(x, y))));
  const g = new THREE.ExtrudeGeometry(shape, { depth: p.length, bevelEnabled: false, curveSegments: 6 });
  g.applyMatrix4(AXIS_MATRIX[p.axis] ?? AXIS_MATRIX.x);
  g.computeVertexNormals();
  return g;
}

export function geometryFor(p) {
  const key = partKey(p);
  let g = geos.get(key);
  if (!g) {
    if (geos.size > LIMIT) { for (const v of geos.values()) v.dispose(); geos.clear(); edges.clear(); }
    g = build(p);
    geos.set(key, g);
  }
  return g;
}

export function edgesFor(p) {
  const key = partKey(p);
  let e = edges.get(key);
  if (!e) {
    e = new THREE.EdgesGeometry(geometryFor(p), 25);
    edges.set(key, e);
  }
  return e;
}

/** Matériau par couleur, état (sélectionné / survolé) et opacité. Aspect selon la catégorie. */
export function materialFor(color, category, state = '', opacity = 1) {
  const op = opacity ?? 1;
  const key = `${color}|${category}|${state}|${op}`;
  let m = mats.get(key);
  if (!m) {
    const metal = category === 'alu' || category === 'rack' || category === 'quincaillerie';
    m = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      metalness: metal ? 0.45 : 0.05,
      roughness: metal ? 0.42 : 0.75,
      emissive: new THREE.Color(state === 'selected' ? '#2c5d8a' : state === 'hover' ? '#1d3247' : '#000000'),
      transparent: op < 1,
      opacity: op,
      depthWrite: op >= 1,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });
    mats.set(key, m);
  }
  return m;
}

const FIX_COLORS = { vis: '#d9b44a', clou: '#b8bec6', boulon: '#8a9099', cheville: '#f0f0f0', tourillon: '#c8a46a', rivet: '#c0c4c8', excentrique: '#d0d0d0', agrafe: '#b8bec6' };

/** Repère visuel d'un emplacement de fixation : tête sur la surface (+Z = côté tête),
 *  tige vers -Z. Grossi au minimum pour rester visible. */
export function fixingGeometry(f) {
  const d = Math.max(2.5, f.d);
  const key = `fix:${f.type}:${r(d)}:${r(f.l)}`;
  let g = geos.get(key);
  if (!g) {
    const head = new THREE.CylinderGeometry(d * 0.95, d * 0.95, Math.max(1, d * 0.45), 14).rotateX(Math.PI / 2).translate(0, 0, d * 0.22);
    const shank = new THREE.CylinderGeometry(d / 2, d / 2, f.l, 10).rotateX(Math.PI / 2).translate(0, 0, -f.l / 2);
    g = mergeTwo(head, shank);
    geos.set(key, g);
  }
  return g;
}

function mergeTwo(a, b) {
  const g = new THREE.BufferGeometry();
  const ai = a.toNonIndexed();
  const bi = b.toNonIndexed();
  for (const name of ['position', 'normal']) {
    const A = ai.getAttribute(name).array;
    const B = bi.getAttribute(name).array;
    const arr = new Float32Array(A.length + B.length);
    arr.set(A, 0);
    arr.set(B, A.length);
    g.setAttribute(name, new THREE.BufferAttribute(arr, 3));
  }
  return g;
}

export function fixingMaterial(type) {
  const key = `fixmat:${type}`;
  let m = mats.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({ color: FIX_COLORS[type] ?? '#d9b44a', metalness: 0.6, roughness: 0.35, emissive: new THREE.Color('#2a2208') });
    mats.set(key, m);
  }
  return m;
}

export const EDGE_MAT = new THREE.LineBasicMaterial({ color: '#0b0d10', transparent: true, opacity: 0.55 });
export const EDGE_MAT_SEL = new THREE.LineBasicMaterial({ color: '#b5d9fd' });
